import { useEffect, useRef, useState } from "react";
import { preferredAudioMimeType, recordedAudioIsAudible } from "./audio";
import { VOICE_MIN_SECONDS, VOICE_MAX_SECONDS, VOICE_PHASE as P } from "./voiceGesture";
import { voiceHaptic } from "./voiceHaptics";

const recordingPhases = [P.RECORDING_HELD, P.RECORDING_LOCKED, P.PAUSED_LOCKED];
const stopStream = (stream) => stream?.getTracks?.().forEach((track) => { track.onended = null; track.stop(); });

// One capture/finalize/upload engine for gesture and existing preview-based recorders.
export default function useVoiceRecording({ onSend, binding, t, gesture = false }) {
  const [view, setView] = useState({ phase: P.IDLE, elapsed: 0, level: null, waveform: null, recording: null, error: "", binding: null, canPause: false });
  const phase = useRef(P.IDLE); const current = useRef(null); const mounted = useRef(true);
  const update = (values) => {
    if (values.phase) {
      phase.current = values.phase;
      if (!recordingPhases.includes(values.phase)) values.waveform = null;
    }
    if (mounted.current) setView((old) => ({ ...old, ...values }));
  };
  const valid = (session) => session && mounted.current && current.current === session && !session.cancelled;
  const elapsed = (session) => (session.elapsedMs + (session.runningSince !== null ? Date.now() - session.runningSince : 0)) / 1000;
  const freeze = (session) => { session.elapsedMs = elapsed(session) * 1000; session.runningSince = null; };
  const release = (session) => {
    clearInterval(session.timer); clearInterval(session.meterTimer);
    try { session.source?.disconnect?.(); } catch (_) { /* already disconnected */ }
    try { session.audioContext?.close?.()?.catch?.(() => {}); } catch (_) { /* meter is optional */ }
    session.audioContext = null;
    stopStream(session.stream); session.stream = null;
  };
  const discard = (session) => {
    if (!session) return;
    session.cancelled = true;
    if (session.recorder) {
      session.recorder.ondataavailable = null; session.recorder.onstop = null;
      session.recorder.onerror = null; session.recorder.onstart = null;
      try { if (session.recorder.state !== "inactive") session.recorder.stop(); } catch (_) { /* cleanup */ }
    }
    release(session);
    if (session.recording?.url) URL.revokeObjectURL(session.recording.url);
  };
  const cancel = (haptic = false, error = "") => {
    const session = current.current;
    if (phase.current === P.SENDING || phase.current === P.PROCESSING) return;
    if (!session || session.cancelled) return;
    discard(session);
    if (haptic && gesture) voiceHaptic("recording_cancel");
    update({ phase: error ? P.FAILED : P.CANCELLED, error, recording: null, elapsed: 0, level: null });
  };
  const reset = () => {
    if ([P.SENDING, P.PROCESSING].includes(phase.current)) return;
    discard(current.current); current.current = null;
    update({ phase: P.IDLE, error: "", recording: null, elapsed: 0, level: null, binding: null });
  };
  const transmit = async (session) => {
    if (!valid(session) || !session.recording || session.sendAttempted) return;
    // No retry after an ambiguous POST: the existing endpoint has no idempotency contract.
    session.sendAttempted = true; update({ phase: P.SENDING, error: "" });
    try {
      const audio = session.recording;
      await session.onSend(audio.blob, audio.duration, audio.type, session.binding);
      if (gesture) voiceHaptic("recording_sent");
      if (!valid(session)) return;
      discard(session); current.current = null;
      update({ phase: P.IDLE, recording: null, elapsed: 0, level: null, binding: null });
    } catch (error) {
      if (valid(session)) update({ phase: P.FAILED, error: error?.message || t("sendFailed"), level: null });
    }
  };
  const finish = (send = true) => {
    const session = current.current;
    if (!valid(session) || !recordingPhases.includes(phase.current) || session.intent) return;
    session.intent = send ? "send" : "preview";
    freeze(session); clearInterval(session.timer); clearInterval(session.meterTimer);
    update({ phase: P.PROCESSING, elapsed: elapsed(session), level: null });
    try { session.recorder.stop(); }
    catch (_) { release(session); update({ phase: P.FAILED, error: t("voiceRecordingFailed") }); }
  };
  const start = async (held = false) => {
    if (![P.IDLE, P.CANCELLED, P.FAILED, P.PREVIEW].includes(phase.current)) return;
    if (phase.current === P.FAILED && current.current?.sendAttempted) return;
    discard(current.current);
    const session = { runningSince: null, elapsedMs: 0, cancelled: false, chunks: [], onSend, waveSamples: [], waveFrame: 0,
      binding: binding ? { ...binding, context: binding.context ? { ...binding.context } : undefined } : null };
    current.current = session;
    update({ phase: P.PERMISSION_PENDING, error: "", recording: null, elapsed: 0, level: null, binding: session.binding, reachedLimit: false });
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      update({ phase: P.FAILED, error: t("voiceUnavailable") }); return;
    }
    // Resume from the user gesture; metering failure never blocks MediaRecorder.
    try {
      const Context = window.AudioContext || window.webkitAudioContext;
      if (Context) { session.audioContext = new Context(); session.audioContext.resume?.()?.catch?.(() => {}); }
    } catch (_) { /* Optional live level unavailable. */ }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!valid(session)) { stopStream(stream); return; }
      session.stream = stream;
      const track = stream.getAudioTracks?.()[0];
      if (!track || !track.enabled || track.muted || track.readyState !== "live") throw new Error("audio-track-unavailable");
      const mimeType = preferredAudioMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined); session.recorder = recorder;
      const interrupted = () => {
        if (!valid(session) || !recordingPhases.includes(phase.current)) return;
        cancel(false, t("voiceInterrupted"));
      };
      track.onended = interrupted;
      recorder.onerror = () => {
        if (!valid(session)) return;
        discard(session); update({ phase: P.FAILED, recording: null, level: null, error: t("voiceRecordingFailed") });
      };
      recorder.ondataavailable = (event) => { if (valid(session) && event.data?.size) session.chunks.push(event.data); };
      recorder.onstart = () => {
        if (!valid(session)) return;
        session.runningSince = Date.now();
        update({ phase: held && !session.lockRequested ? P.RECORDING_HELD : P.RECORDING_LOCKED,
          canPause: typeof recorder.pause === "function" && typeof recorder.resume === "function" });
        if (gesture) voiceHaptic("recording_start");
        if (gesture && session.lockRequested) voiceHaptic("recording_lock");
        session.timer = setInterval(() => {
          if (!valid(session)) return;
          if (elapsed(session) >= VOICE_MAX_SECONDS) {
            session.elapsedMs = VOICE_MAX_SECONDS * 1000; session.runningSince = null;
            update({ reachedLimit: true }); finish(false);
          } else update({ elapsed: elapsed(session) });
        }, 200);
        try {
          if (!session.audioContext) return;
          const analyser = session.audioContext.createAnalyser(); analyser.fftSize = 256;
          session.source = session.audioContext.createMediaStreamSource(stream); session.source.connect(analyser);
          const samples = new Uint8Array(analyser.fftSize);
          session.meterTimer = setInterval(() => {
            if (!valid(session)) return;
            if (phase.current === P.PAUSED_LOCKED || session.audioContext?.state !== "running") { update({ level: 0 }); return; }
            analyser.getByteTimeDomainData(samples);
            const rms = Math.sqrt(samples.reduce((sum, n) => sum + ((n - 128) / 128) ** 2, 0) / samples.length);
            // Preserve real positive/negative peaks, with bounded visual gain.
            // No random oscillation or minimum amplitude is added during silence.
            const high = Math.max(0, (Math.max(...samples) - 128) / 128);
            const low = Math.min(0, (Math.min(...samples) - 128) / 128);
            session.waveSamples = [...session.waveSamples, Math.min(1, high * 5), Math.max(-1, low * 5)].slice(-64);
            update({ level: Math.min(1, rms * 5), waveform: { samples: session.waveSamples, frame: ++session.waveFrame } });
          }, 80);
        } catch (_) { update({ level: null }); }
      };
      recorder.onstop = async () => {
        if (!valid(session) || session.finalized) return;
        session.finalized = true;
        if (!session.intent) { discard(session); update({ phase: P.FAILED, error: t("voiceInterrupted"), level: null }); return; }
        release(session);
        const duration = elapsed(session); const type = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(session.chunks, { type }); session.chunks = [];
        let error = duration < VOICE_MIN_SECONDS ? t("voiceTooShort") : !blob.size ? t("voiceRecordingEmpty") : "";
        if (!error) {
          try { if (!(await recordedAudioIsAudible(blob))) error = t("voiceRecordingSilent"); }
          catch (_) { error = t("voiceRecordingFailed"); }
        }
        if (!valid(session)) return;
        if (error) { update({ phase: P.FAILED, error }); return; }
        session.recording = { blob, duration, type, url: URL.createObjectURL(blob) };
        update({ recording: session.recording, elapsed: duration });
        if (session.intent === "send") await transmit(session);
        else update({ phase: P.PREVIEW });
      };
      recorder.start();
    } catch (error) {
      if (!valid(session)) return;
      discard(session);
      update({ phase: P.FAILED, error: t(error?.name === "NotAllowedError" ? "microphonePermission" : "voiceRecordingFailed") });
    }
  };
  const lock = () => {
    if (phase.current === P.PERMISSION_PENDING && valid(current.current)) {
      current.current.lockRequested = true;
      return;
    }
    if (phase.current !== P.RECORDING_HELD) return;
    update({ phase: P.RECORDING_LOCKED }); if (gesture) voiceHaptic("recording_lock");
  };
  const pause = () => {
    const session = current.current;
    if (!valid(session)) return;
    try {
      if (phase.current === P.RECORDING_LOCKED && session.recorder.state === "recording") {
        session.recorder.pause(); freeze(session); update({ phase: P.PAUSED_LOCKED, elapsed: elapsed(session), level: 0 });
      } else if (phase.current === P.PAUSED_LOCKED && session.recorder.state === "paused") {
        session.recorder.resume(); session.runningSince = Date.now(); update({ phase: P.RECORDING_LOCKED });
      }
    } catch (_) { update({ canPause: false }); }
  };
  const interruptRef = useRef(null);
  useEffect(() => {
    if (view.phase !== P.CANCELLED) return undefined;
    const timer = setTimeout(() => {
      if (phase.current === P.CANCELLED) update({ phase: P.IDLE, binding: null });
    }, 900);
    return () => clearTimeout(timer);
  }, [view.phase]);
  interruptRef.current = () => {
    if ([P.PERMISSION_PENDING, ...recordingPhases].includes(phase.current)) cancel(false, t("voiceInterrupted"));
  };
  useEffect(() => {
    mounted.current = true;
    const hidden = () => { if (document.hidden) interruptRef.current(); };
    // Permission prompts can blur the window: only interrupt already-running capture on blur.
    const blur = () => { if (recordingPhases.includes(phase.current)) interruptRef.current(); };
    const pagehide = () => interruptRef.current();
    document.addEventListener("visibilitychange", hidden); window.addEventListener("blur", blur); window.addEventListener("pagehide", pagehide);
    return () => {
      mounted.current = false; discard(current.current);
      document.removeEventListener("visibilitychange", hidden); window.removeEventListener("blur", blur); window.removeEventListener("pagehide", pagehide);
    };
    // Session refs own asynchronous handlers and cleanup; no effect restart during a gesture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { ...view, phaseRef: phase, start, finish, lock, cancel, reset, pause, send: () => transmit(current.current) };
}
